import {styled} from '@linaria/react';
import React, {RefObject} from 'react';
import {
  mediaDesktopAndUp,
  mediaExtraLargeScreens,
  mediaLargeScreens,
} from '../../../constants/customMedia';
import {gridColsWidth} from '../../../constants/gridSystem';
import {RowSectionProps} from '../SplitView/RowSection';
import {RowSectionGrid} from './RowSectionGrid';

type SplitType = '6:5' | '4:8' | '2:10';

interface SplitViewProps extends RowSectionProps, ComponentSplit {
  contentStart: React.ReactNode;
  contentEnd: React.ReactNode;
  refStart?: React.RefObject<HTMLDivElement | null>;
  refEnd?: React.RefObject<HTMLDivElement | null>;
  className?: string;
  ref?: RefObject<HTMLElement | null>;
}

interface ComponentSplit {
  split?: SplitType;
  splitMedium?: SplitType;
  splitLarge?: SplitType;
  splitExtraLarge?: SplitType;
}

export const SplitViewGrid: React.FC<SplitViewProps> = ({
  dataTestId,
  contentStart,
  contentEnd,
  refStart,
  refEnd,
  split,
  splitMedium,
  splitLarge,
  splitExtraLarge,
  ref,
  ...props
}) => {
  return (
    <StyledRowSectionGrid
      data-testid={dataTestId || 'splitView'}
      ref={ref as RefObject<HTMLElement | null>}
      {...props}
    >
      <SectionStart
        data-testid="sectionStart"
        ref={refStart}
        split={split}
        splitMedium={splitMedium}
        splitLarge={splitLarge}
        splitExtraLarge={splitExtraLarge}
      >
        {contentStart}
      </SectionStart>
      <SectionEnd
        data-testid="sectionEnd"
        ref={refEnd}
        split={split}
        splitMedium={splitMedium}
        splitLarge={splitLarge}
      >
        {contentEnd}
      </SectionEnd>
    </StyledRowSectionGrid>
  );
};

const ComponentSplitMap: {
  [split in SplitType]: {
    Start: number;
    End: number;
  };
} = {
  '6:5': {
    Start: 6,
    End: 5,
  },
  '4:8': {
    Start: 4,
    End: 8,
  },
  '2:10': {
    Start: 2,
    End: 10,
  },
};

const SectionStart = styled.div<ComponentSplit>`
  --width: ${({split}) =>
    split
      ? gridColsWidth(ComponentSplitMap[split].Start)
      : gridColsWidth(ComponentSplitMap['6:5'].Start)};

  --width-desktop-and-up: ${({splitMedium}) =>
    splitMedium
      ? gridColsWidth(ComponentSplitMap[splitMedium].Start)
      : 'var(--width)'};

  --width-large-screens: ${({splitLarge}) =>
    splitLarge
      ? gridColsWidth(ComponentSplitMap[splitLarge].Start)
      : 'var(--width-desktop-and-up)'};

  --width-extra-large-screens: ${({splitExtraLarge}) =>
    splitExtraLarge
      ? gridColsWidth(ComponentSplitMap[splitExtraLarge].Start)
      : 'var(--width-large-screens)'};

  width: var(--width);

  @media ${mediaDesktopAndUp} {
    width: var(--width-desktop-and-up);
  }

  @media ${mediaLargeScreens} {
    width: var(--width-large-screens);
  }

  @media ${mediaExtraLargeScreens} {
    width: var(--width-extra-large-screens);
  }
`;

const SectionEnd = styled.div<ComponentSplit>`
  --width: ${({split}) =>
    split
      ? gridColsWidth(ComponentSplitMap[split].End)
      : gridColsWidth(ComponentSplitMap['6:5'].End)};

  --width-desktop-and-up: ${({splitMedium}) =>
    splitMedium
      ? gridColsWidth(ComponentSplitMap[splitMedium].End)
      : 'var(--width)'};

  --width-large-screens: ${({splitLarge}) =>
    splitLarge
      ? gridColsWidth(ComponentSplitMap[splitLarge].End)
      : 'var(--width-desktop-and-up)'};

  --width-extra-large-screens: ${({splitExtraLarge}) =>
    splitExtraLarge
      ? gridColsWidth(ComponentSplitMap[splitExtraLarge].End)
      : 'var(--width-large-screens)'};

  width: var(--width);

  @media ${mediaDesktopAndUp} {
    width: var(--width-desktop-and-up);
  }

  @media ${mediaLargeScreens} {
    width: var(--width-large-screens);
  }

  @media ${mediaExtraLargeScreens} {
    width: var(--width-extra-large-screens);
  }
`;

const StyledRowSectionGrid = styled(RowSectionGrid)`
  justify-content: space-between;
`;
